import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "lib/router";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { ChevronDown, LogOut, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import { SvgIcon } from "assets";
import { QaydLogo, QaydMark } from "components/Brand";
import { filterNavByFeatures } from "config/features";
import { useAuth } from "context/UseContext";
import { setAuthString } from "auth/authStorage";
import { setEmployeeAuthentication } from "redux/slices/navigationDataSlice";
import { ErrorToaster, SuccessToaster } from "components/Toaster";
import ClientServices from "services/Client";
import SystemServices from "services/System";
import AuthServices from "services/Auth";
import CustomerServices from "services/Customer";
import VehicleBookingServices from "services/VehicleBooking";
import { cn, ConfirmationDialog, InputField, PrimaryButton, SelectField, SimpleDialog } from "ui";
import { isChildVisible, isItemActive } from "./navFilters";

const subCustomer = [
  { name: "My Customers", route: "/my-customers", icon: "customer", childRoute: [""], children: [] },
  { name: "My Vehicles", route: "/my-vehicles", icon: "vehicle", childRoute: [""], children: [] },
];

// storage key → field in the system/web-tokens response
const WEB_TOKENS = {
  financeToken: "financeToken",
  vccToken: "vccToken",
  operationsToken: "operationsToken",
  searchToken: "searchToken",
  walletToken: "walletToken",
  buyerIdToken: "buyerIdToken",
  managementReports: "managementReports",
  exportReports: "exportReports",
  shippingReports: "shippingReport",
  muhammadHusniReport: "muhammadHusniReport",
  anassFahmouneReport: "anassFahmouneReport",
  awsALiJrdyReport: "awsALiJrdyReport",
  koulMabroukReport: "koulMabroukReport",
  rehmatUllahKhanReport: "rehmatUllahKhanReport",
  ravinAbdulkareemReport: "ravinAbdulkareemReport",
  shohratAshyrovReport: "shohratAshyrovReport",
  ardamehrShoevReport: "ardamehrShoevReport",
  rafiMahmoodReport: "rafiMahmoodReport",
  riazMuhammadReport: "riazMuhammadReport",
  saqibUmerReport: "saqibUmerReport",
  BuyerIdAnalysisReport: "BuyerIdAnalysisReport",
  AgentBotReport: "AgentBotReport",
  ClaimVehicleMobileReport: "ClaimVehicleMobileReport",
  TTAnalysisReport: "TTAnalysisReport",
  rashidKpisDashboard: "rashidKpisReport",
  allMarketingTeamReport: "allMarketingTeamReport",
  NawarYaseerHlebeahReport: "NawarYaseerHlebeahReport",
  finance2Report: "finance2Report",
};

function NavIcon({ iconKey }) {
  const Icon = SvgIcon[iconKey];
  return (
    <span className="grid size-5 shrink-0 place-items-center [&_path]:fill-current [&_svg]:size-[18px]">
      {Icon ? <Icon /> : <span className="size-1.5 rounded-full bg-current" />}
    </span>
  );
}

function NavItem({ item, pathname, open, onToggle, collapsed, children }) {
  const active = isItemActive(item, pathname);
  const hasChildren = Array.isArray(item?.children) && item.children.length > 0;
  const Comp = item.route ? Link : "button";
  return (
    <li>
      <Comp
        {...(item.route ? { to: item.route } : { type: "button" })}
        aria-label={item?.name}
        title={collapsed ? item?.name : undefined}
        onClick={onToggle}
        className={cn(
          "flex h-9 w-full items-center gap-2.5 rounded-lg px-2.5 text-left text-[13px] capitalize transition-colors",
          active ? "bg-white/[0.09] text-white" : "text-white/65 hover:bg-white/[0.06] hover:text-white",
          collapsed && "justify-center px-0",
        )}
      >
        <span className={cn(active && "text-[#a5a5ff]")}>
          <NavIcon iconKey={item.icon} />
        </span>
        {!collapsed && <span className="flex-1 truncate">{item.name}</span>}
        {!collapsed && hasChildren && <ChevronDown className={cn("size-4 shrink-0 text-white/40 transition-transform", open && "rotate-180")} />}
      </Comp>
      {!collapsed && hasChildren && open && <ul className="mt-0.5 mb-1 ml-[21px] space-y-px border-l border-white/10 pl-2.5">{children}</ul>}
    </li>
  );
}

function SubItem({ child, pathname, onClick }) {
  const active = isItemActive(child, pathname);
  const Comp = child.route ? Link : "button";
  return (
    <li>
      <Comp
        {...(child.route ? { to: child.route } : { type: "button" })}
        aria-label={child?.name}
        onClick={onClick}
        className={cn(
          "block w-full truncate rounded-md px-2 py-1.5 text-left text-[12.5px] capitalize transition-colors",
          active ? "font-semibold text-white" : "text-white/55 hover:bg-white/[0.05] hover:text-white",
        )}
      >
        {child.name}
      </Comp>
    </li>
  );
}

function Sidebar({ collapsed, onToggle }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user, userLogout } = useAuth();
  const { pathname } = useLocation();
  const { register, handleSubmit, getValues } = useForm();
  const { register: register2, handleSubmit: handleSubmit2, getValues: getValues2, reset: reset2 } = useForm({ defaultValues: { vinSearch: "" } });

  const { navigation: rawNavigation } = useSelector((state) => state.navigationReducer);
  // Hide disabled product features (e.g. Export) from the menu
  const navigation = filterNavByFeatures(rawNavigation);
  const [expand, setExpand] = useState([]);

  const [vinDialog, setVinDialog] = useState(false);
  const [changeUserNameDialog, setChangeUserNameDialog] = useState(false);
  const [changeNameDialog, setChangeNameDialog] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState(false);
  const [vinData, setVinData] = useState();
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [changeReason, setChangeReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [loader, setLoader] = useState(false);
  const [vehiclePaymentStatus, setVehiclePaymentStatus] = useState("");
  const [isUnpaid, setIsUnpaid] = useState(false);

  const handleLogout = async () => {
    try {
      await AuthServices.handleLogout({ fcm_token: localStorage.getItem("fcmToken") });
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const handleCollapse = (value) => setExpand((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [value]));
  const isOpen = (item) => expand.includes(item.name) || (Array.isArray(item.childRoute) && item.childRoute.indexOf(pathname) !== -1);

  // *For Search Vin/Lot → open the booking
  const getVehicleTT = async (filter) => {
    try {
      const { data } = await ClientServices.getVehicleTT({ page: 1, limit: 15, ...filter });
      if (data?.details) {
        navigate(`/vehicle-booking-detail/${data?.details?.id}`);
        setVinDialog(false);
      } else {
        ErrorToaster("No Vehicle Found");
      }
    } catch (error) {
      ErrorToaster(error);
    }
  };

  // *For Change Customer Name → look up the vehicle first
  const getVehicleTTData = async (filter) => {
    setLoader(true);
    try {
      const { data } = await ClientServices.getVehicleTT({ page: 1, limit: 15, ...filter });
      const inv = data?.details?.invoice || {};
      const isPaid = Number(inv.amount ?? 0) > 0 && Number(inv.paid ?? 0) >= Number(inv.amount ?? 0);
      if (data?.details) {
        setVinData(data?.details);
        setChangeUserNameDialog(false);
        if (isPaid) {
          ErrorToaster("This VIN Is Already Paid So Cannot Change Its Customer Name.");
          setChangeNameDialog(false);
          return;
        }
        setChangeNameDialog(true);
      } else {
        ErrorToaster("No Vehicle Found");
      }
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setLoader(false);
    }
  };

  const getCustomerBooking = async (search) => {
    try {
      const { data } = await CustomerServices.getCustomerBooking({ name: search ?? "" });
      setCustomers(data?.customers);
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const getWebTokens = async () => {
    try {
      const { data } = await SystemServices.getWebTokens();
      Object.entries(WEB_TOKENS).forEach(([key, field]) => setAuthString(key, data?.[field]));
    } catch (error) {
      ErrorToaster(error);
    }
  };

  const updateVehicleBooking = async () => {
    setLoading(true);
    if (vinData?.customer?.name !== "UNKNOWN") {
      ErrorToaster("This Vehicle is already assigned to actual customer, please consult with Finance team for change.");
      setLoading(false);
      return;
    }
    try {
      const customerData = {
        booking_id: vinData?.id,
        customer_id: selectedCustomer?.id,
        customer_name: selectedCustomer?.name,
        change_reason: changeReason,
      };
      const { message } = await VehicleBookingServices.updateVehicleBooking({ customerData });
      setChangeNameDialog(false);
      SuccessToaster(message);
      window.location.reload();
    } catch (error) {
      ErrorToaster(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getWebTokens();
  }, []);

  useEffect(() => {
    if (vinData?.invoice?.paid === "0.000") {
      setVehiclePaymentStatus("Unpaid");
      setIsUnpaid(true);
    } else if (vinData?.invoice?.paid === vinData?.invoice?.amount) {
      setVehiclePaymentStatus("Paid");
      setIsUnpaid(false);
    }
  }, [vinData]);

  useEffect(() => {
    if (changeUserNameDialog) reset2({ vinSearch: "" });
  }, [changeUserNameDialog, reset2]);

  const onChildClick = (child) => {
    if (child?.name === "Search Vin/Lot") setVinDialog(true);
    else if (child?.name === "Change Customer Name") setChangeUserNameDialog(true);
    else if (child.route) handleCollapse(child.name);
  };

  const userTypeLabel = user?.user_type === "A" ? "Admin ID" : user?.user_type === "S" ? "Staff ID" : "Customer ID";
  const initials = (user?.name || "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <>
      <ConfirmationDialog
        open={confirmationDialog}
        onClose={() => setConfirmationDialog(false)}
        message="Are you sure you want to logout?"
        confirmLabel="Log out"
        action={() => {
          setConfirmationDialog(false);
          dispatch(setEmployeeAuthentication(false));
          userLogout();
          handleLogout();
        }}
      />

      {/* Change customer name, step 1: find the vehicle */}
      <SimpleDialog
        open={changeUserNameDialog}
        onClose={() => {
          setChangeUserNameDialog(false);
          reset2({ search: "" });
        }}
        title="Change Customer Name"
      >
        <form onSubmit={handleSubmit2(() => getVehicleTTData({ search: getValues2("vinSearch") }))}>
          <InputField label="Search/Vin/Lot" placeholder="VIN or lot number" register={register2("vinSearch")} />
          <div className="flex justify-end">
            <PrimaryButton title="Find vehicle" type="submit" loading={loader} />
          </div>
        </form>
      </SimpleDialog>

      {/* Change customer name, step 2: choose the new customer */}
      <SimpleDialog open={changeNameDialog} onClose={() => setChangeNameDialog(false)} title="Change Customer Name">
        <form onSubmit={handleSubmit(updateVehicleBooking)}>
          <div className="grid grid-cols-2 gap-x-4">
            <InputField label="Current Customer Name" value={vinData?.customer?.name} disabled />
            <InputField label="VIN" value={vinData?.vin} disabled />
            <InputField label="LOT" value={vinData?.lot_number} disabled />
            <InputField label="Buyer ID" value={vinData?.buyer_id} disabled />
            <InputField label="Vehicle Payment Status" value={vehiclePaymentStatus} disabled />
            {isUnpaid && (
              <>
                <SelectField
                  onSearch={(v) => getCustomerBooking(v)}
                  label="Select New Customer Name"
                  options={customers}
                  placeholder="New Customer Name"
                  selected={selectedCustomer}
                  onSelect={(value) => setSelectedCustomer(value)}
                  register={register("customerName")}
                />
                <InputField label="Change By" value={user?.name} disabled />
                <InputField label="Change Reason" placeholder="Why is the customer changing?" multiline value={changeReason} onChange={(e) => setChangeReason(e.target.value)} />
              </>
            )}
          </div>
          <div className="flex justify-end">
            <PrimaryButton title="Submit" type="submit" loading={loading} disabled={!isUnpaid || !selectedCustomer || !changeReason?.trim()} />
          </div>
        </form>
      </SimpleDialog>

      <SimpleDialog open={vinDialog} onClose={() => setVinDialog(false)} title="Search By Vin/Lot">
        <form onSubmit={handleSubmit(() => getVehicleTT({ search: getValues("search"), dashboard: true }))}>
          <InputField label="Search" placeholder="VIN or lot number" register={register("search")} />
          <div className="flex justify-end">
            <PrimaryButton title="Search" type="submit" />
          </div>
        </form>
      </SimpleDialog>

      <div className="flex h-full flex-col">
        {/* Brand + collapse */}
        <div className={cn("flex h-14 shrink-0 items-center border-b border-white/[0.07]", collapsed ? "justify-center" : "justify-between px-4")}>
          {collapsed ? (
            <button type="button" onClick={onToggle} aria-label="Expand sidebar" className="grid size-9 place-items-center rounded-lg hover:bg-white/[0.06]">
              <QaydMark size={24} />
            </button>
          ) : (
            <>
              <QaydLogo tone="white" size={26} />
              <button type="button" onClick={onToggle} aria-label="Collapse sidebar" className="grid size-8 place-items-center rounded-lg text-white/50 hover:bg-white/[0.06] hover:text-white">
                <PanelLeftClose className="size-[18px]" />
              </button>
            </>
          )}
        </div>

        {/* Menu */}
        <nav className={cn("min-h-0 flex-1 overflow-y-auto py-3 [scrollbar-color:rgba(255,255,255,0.12)_transparent] [scrollbar-width:thin]", collapsed ? "px-2" : "px-3")}>
          <ul className="space-y-0.5">
            {user?.user_type == "C" && <NavItem item={{ name: "Dashboard", route: "/dashboard", icon: "home" }} pathname={pathname} collapsed={collapsed} />}

            {user?.user_type == "C" &&
              user?.customer_type_id == 3 &&
              user?.journey?.request?.status == "Completed" &&
              subCustomer.map((item) => <NavItem key={item.name} item={item} pathname={pathname} collapsed={collapsed} />)}

            {user?.user_type != "C" &&
              navigation.map((item) => {
                const children = (item?.children || []).filter((child) => isChildVisible(child, user));
                return (
                  <NavItem
                    key={item.name}
                    item={{ ...item, children }}
                    pathname={pathname}
                    collapsed={collapsed}
                    open={isOpen(item)}
                    onToggle={() => {
                      if (collapsed && children.length) onToggle();
                      if (item.childRoute) handleCollapse(item.name);
                    }}
                  >
                    {children.map((child) => (
                      <SubItem key={child.name + (child.route || "")} child={child} pathname={pathname} onClick={() => onChildClick(child)} />
                    ))}
                  </NavItem>
                );
              })}
          </ul>
        </nav>

        {/* Signed-in user */}
        <div className={cn("shrink-0 border-t border-white/[0.07] p-3", collapsed && "px-2")}>
          {collapsed ? (
            <div className="flex flex-col items-center gap-2">
              <button type="button" onClick={onToggle} aria-label="Expand sidebar" className="grid size-9 place-items-center rounded-lg text-white/50 hover:bg-white/[0.06] hover:text-white">
                <PanelLeftOpen className="size-[18px]" />
              </button>
              <button type="button" onClick={() => setConfirmationDialog(true)} aria-label="Log out" className="grid size-9 place-items-center rounded-lg text-white/50 hover:bg-white/[0.06] hover:text-white">
                <LogOut className="size-[18px]" />
              </button>
            </div>
          ) : (
            <div className="flex items-center gap-2.5">
              {user?.picture ? (
                <img src={process.env.REACT_APP_IMAGE_BASE_URL + user.picture} alt="" className="size-9 shrink-0 rounded-full object-cover ring-1 ring-white/15" />
              ) : (
                <span className="grid size-9 shrink-0 place-items-center rounded-full bg-accent text-[12px] font-semibold text-white">{initials}</span>
              )}
              <div className="min-w-0 flex-1">
                <div className="truncate text-[13px] font-medium text-white">{user?.name}</div>
                <div className="truncate text-[11.5px] text-white/45">
                  {userTypeLabel} · {user?.ref_id}
                </div>
              </div>
              <button type="button" onClick={() => setConfirmationDialog(true)} aria-label="Log out" title="Log out" className="grid size-8 shrink-0 place-items-center rounded-lg text-white/50 hover:bg-white/[0.06] hover:text-white">
                <LogOut className="size-4" />
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default Sidebar;
